import LogManager from "./LogManager";

const RETRY_DELAY_MS = 2000;

/**
 * Formats an error into a loggable string with name, message, and status if available.
 * @param {Error} error - The error to format
 * @param {Response} [response] - Optional fetch Response for status info
 * @returns {string} Formatted error string
 */
const formatError = (error, response) => {
  const parts = [`${error.name}: ${error.message}`];
  if (response) {
    parts.push(`status=${response.status}`);
  }
  return parts.join(", ");
};

/**
 * Determines if a failed request should be retried.
 * Retries on network errors (no response) and 5xx server errors.
 * Does not retry on 4xx client errors (bad request, validation failure).
 * @param {Error} error - The error that occurred
 * @param {Response} [response] - Optional fetch Response
 * @returns {boolean} Whether the request should be retried
 */
const isRetryable = (error, response) => {
  if (!response) return true; // Network error — fetch itself threw
  return response.status >= 500;
};

/**
 * Utility function to call the rtms-status-update API with a single retry
 * for transient network or server errors.
 * @param {string} action - The action to perform ("start", "stop", "started")
 * @param {string} meetingUuid - The meeting UUID
 * @param {string} meetingId - The meeting ID
 * @param {string} role - The user role
 * @param {string} appContext - The app context JWT string from Zoom SDK for secure backend validation
 * @returns {Promise<Object>} The parsed response from the API
 */
export const fetchRtmsStatusUpdate = async (action, meetingUuid, meetingId, role, appContext) => {
  const data = {
    action,
    role,
    meetingUuid,
    meetingId,
    requestId: crypto.randomUUID(),
    context: appContext,
  };

  LogManager.info(`Calling /rtms-status-update with action "${action}". Request body is: ${JSON.stringify(data)}`);

  let lastError;
  let lastResponse;

  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      if (attempt > 0) {
        LogManager.info(`Retrying /rtms-status-update (${action}) after ${RETRY_DELAY_MS}ms delay`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
      }

      lastResponse = await fetch("/rtms-status-update", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const responseJson = await lastResponse.json();
      LogManager.info(`The responseJson of /rtms-status-update (${action}) is: `, responseJson);

      let parsedPayload = JSON.parse(responseJson.Payload);
      LogManager.info(`Parsed RtmsResponse payload from ${action} result is:`, parsedPayload);

      const rtmsResponse = JSON.parse(parsedPayload);

      if (rtmsResponse.result === "OK") {
        LogManager.info(`RTMS status update (${action}) successful`);
      } else {
        LogManager.warn(`RTMS status update (${action}) returned non-OK result: ${rtmsResponse.result}`);
      }

      return rtmsResponse;
    } catch (error) {
      lastError = error;
      LogManager.error(`Error calling /rtms-status-update with ${action} action (attempt ${attempt + 1}): ${formatError(error, lastResponse)}`);

      if (attempt === 0 && isRetryable(error, lastResponse)) {
        continue;
      }
      throw error;
    }
  }

  throw lastError;
};
